import { truncateString } from '@kwenta/sdk/utils'
import { ChangeEvent, FC, memo, useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import Input from 'components/Input/Input'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectSelectedSubAccountAddress } from 'state/futures/common/selectors'
import { renameAddress, setNewNickname } from 'state/futures/reducer'
import {
	selectAddressBook,
	selectNewNickname,
	selectSelectedDelegationAddress,
} from 'state/futures/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectSignerWallet } from 'state/wallet/selectors'

import { DelegationSubTab } from './DelegationTradingTab'

type Props = {
	type: 'subaccount' | 'delegate'
	onDismiss(): void
}

const RenameAddressModal: FC<Props> = memo(({ type = 'delegate', onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const wallet = useAppSelector(selectSignerWallet)
	const newNickname = useAppSelector(selectNewNickname)
	const selectedSubAccountAddress = useAppSelector(selectSelectedSubAccountAddress)
	const selectedDelegationAddress = useAppSelector(selectSelectedDelegationAddress)
	const selectedAddress = useMemo(
		() => (type === 'delegate' ? selectedDelegationAddress : selectedSubAccountAddress),
		[selectedDelegationAddress, selectedSubAccountAddress, type]
	)
	const addressBook = useAppSelector(selectAddressBook)
	const oldNickname = useMemo(() => {
		if (selectedAddress) {
			const addressBookEntry = addressBook.find(
				(entry) => entry.address.toLowerCase() === selectedAddress.toLowerCase()
			)
			return addressBookEntry?.nickname
		}
		return 'Old Name'
	}, [addressBook, selectedAddress])

	useEffect(() => {
		dispatch(setNewNickname(''))
	}, [dispatch])

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			dispatch(setNewNickname(e.target.value))
		},
		[dispatch]
	)

	const onSave = useCallback(() => {
		const delegate = { address: selectedAddress, nickname: newNickname }
		if (wallet) {
			dispatch(renameAddress({ wallet, delegate }))
		}
		dispatch(
			setOpenModalWithParams({
				type: 'futures_deposit_withdraw_smart_margin',
				params: {
					tab: ManageModalType.Delegation,
					subTab: type === 'delegate' ? DelegationSubTab.Delegations : DelegationSubTab.SubAccounts,
				},
			})
		)
	}, [dispatch, newNickname, selectedAddress, type, wallet])

	return (
		<StyledBaseModal
			title={
				<Trans
					i18nKey="futures.market.trade.margin.modal.delegation.rename.modal-title"
					components={[<NameLabel />]}
					values={{ oldNickname: oldNickname ? truncateString(oldNickname, 25) : '' }}
				/>
			}
			isOpen
			onDismiss={onDismiss}
		>
			<Spacer height={10} />
			<Body size="small" color="secondary">
				{t('futures.market.trade.margin.modal.delegation.rename.input-label')}
			</Body>
			<Spacer height={10} />
			<Input value={newNickname} onChange={onChange} placeholder={oldNickname} />
			<Spacer height={30} />
			<TransactionButton
				size="small"
				variant="flat"
				disabled={!newNickname}
				fullWidth
				onClick={onSave}
			>
				{t('futures.market.trade.margin.modal.delegation.rename.save-button')}
			</TransactionButton>
		</StyledBaseModal>
	)
})

const NameLabel = styled.span`
	margin-left: 5px;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
		margin-top: 300px;
	}
`

export default RenameAddressModal
